<template>
  <section class="aps-cpd-results l-gridWrap">
    <p class="aps-cpd-results__count">
      <span v-if="!isLoadingCards"> {{ numResults }} {{ numResults === 1 ? 'result' : 'results' }} </span>
    </p>

    <div class="aps-cpd-results__form">
      <el-select
        name="searchInput"
        ref="$searchInput"
        v-model="searchInput"
        placeholder="Enter one or more phrases"
        :suffix-icon="null"
        :popper-append-to-body="false"
        multiple
        filterable
        allow-create
        default-first-option
        @change="setURLSearchParams"
        @blur="captureInput"
      >
      </el-select>
      <button class="aps-cpd-results__submit" @click="handleSearch">Find</button>
    </div>

    <div class="aps-filters">
      <div class="aps-filters__dropdowns" :class="{ 'aps-filters__dropdowns--expanded': isFiltersExpanded }">
        <button class="aps-filters__toggle" @click.prevent="isFiltersExpanded = !isFiltersExpanded">
          <h5>Filters</h5>
          <svg class="aps-dropdown__chevron">
            <use xlink:href="#icon-chevron" />
          </svg>
        </button>

        <el-dropdown ref="dropdownType" class="el-dropdown--type" placement="bottom-start" trigger="click" :hide-on-click="false" @visible-change="showOverlay">
          <span class="aps-dropdown" :class="{ 'aps-dropdown--selected': filters.type.length }">
            Type
            <svg class="aps-dropdown__chevron">
              <use xlink:href="#icon-chevron" />
            </svg>
          </span>
          <template #dropdown>
            <div class="aps-dropdown__header">
              <span class="aps-dropdown__title">Type</span>
              <button class="aps-dropdown__close" @click="closeDropdown('dropdownType')">
                <svg>
                  <use xlink:href="#icon-close" />
                </svg>
              </button>
            </div>
            <el-dropdown-menu>
              <div class="el-dropdown-menu__header">
                <h5>Select one or multiple</h5>
                <button @click="filters.type = []">Reset</button>
              </div>
              <el-checkbox-group v-model="filters.type">
                <el-checkbox v-for="(type, index) in eventTypes" :key="index" :label="type"></el-checkbox>
              </el-checkbox-group>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <el-dropdown ref="dropdownFormat" class="el-dropdown--format" trigger="click" @visible-change="showOverlay" :hide-on-click="false">
          <span class="aps-dropdown" :class="{ 'aps-dropdown--selected': filters.format !== 'All' }">
            Format
            <svg class="aps-dropdown__chevron">
              <use xlink:href="#icon-chevron" />
            </svg>
          </span>
          <template #dropdown>
            <div class="aps-dropdown__header">
              <span class="aps-dropdown__title">Format</span>
              <button class="aps-dropdown__close" @click="closeDropdown('dropdownFormat')">
                <svg>
                  <use xlink:href="#icon-close" />
                </svg>
              </button>
            </div>
            <el-dropdown-menu>
              <el-radio-group v-model="filters.format">
                <el-radio label="All">All</el-radio>
                <el-radio label="Online">Online</el-radio>
                <el-radio label="In-person">In person</el-radio>
              </el-radio-group>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <el-dropdown ref="dropdownOrganiser" class="el-dropdown--organiser" trigger="click" :hide-on-click="false" @visible-change="showOverlay">
          <span class="aps-dropdown" :class="{ 'aps-dropdown--selected': filters.organiser !== 'All' }">
            Organiser
            <svg class="aps-dropdown__chevron">
              <use xlink:href="#icon-chevron" />
            </svg>
          </span>
          <template #dropdown>
            <div class="aps-dropdown__header">
              <span class="aps-dropdown__title">Organiser</span>
              <button class="aps-dropdown__close" @click="closeDropdown('dropdownOrganiser')">
                <svg>
                  <use xlink:href="#icon-close" />
                </svg>
              </button>
            </div>
            <el-dropdown-menu>
              <el-radio-group v-model="filters.organiser">
                <el-radio v-for="key in Object.keys(organiserItems)" :key="key" :label="key">{{ organiserItems[key] }}</el-radio>
              </el-radio-group>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <el-date-picker
          v-model="filters.dateRange"
          :class="{ 'el-date-editor--selected': !!filters.dateRange }"
          type="daterange"
          prefix-icon="blank"
          range-separator=""
          start-placeholder="Start date"
          end-placeholder="End date"
          popper-class="el-popper--date-range"
          :disabled-date="isDisabledDate"
          clearable
        >
        </el-date-picker>

        <el-select
          v-model="filters.location"
          value-key="id"
          class="el-select--location"
          placeholder="Location"
          @click="setFocus"
          :remote-method="getLocations"
          default-first-option
          clearable
          filterable
          remote
          @clear="clearLocations"
        >
          <el-option v-for="l in locations" :key="l.id" :label="`${l.town}, ${l.state} ${l.postCode}`" :value="l"> </el-option>
          <template #prefix>
            <svg>
              <use xlink:href="#icon-location" />
            </svg>
          </template>
        </el-select>

        <el-checkbox class="aps-filters__approved aps-filters__approved--ms" v-model="filters.isApproved" label="APS CPD Approved" size="large" />
      </div>

      <el-checkbox class="aps-filters__approved aps-filters__approved--ds" v-model="filters.isApproved" label="APS CPD Approved" size="large" />

      <div v-if="filters.type.length || filters.format !== 'All' || filters.organiser !== 'All' || filters.dateRange" class="aps-filters__tags">
        <el-tag v-for="(tag, i) in filters.type" :key="tag" type="info" closable @close="filters.type.splice(i, 1)">
          {{ tag }}
        </el-tag>
        <el-tag v-if="filters.format !== 'All'" type="info" closable @close="filters.format = 'All'">
          {{ filters.format }}
        </el-tag>
        <el-tag v-if="filters.organiser !== 'All'" type="info" closable @close="filters.organiser = 'All'">
          {{ organiserItems[filters.organiser] }}
        </el-tag>
        <el-tag v-if="filters.dateRange" type="info" closable @close="filters.dateRange = null">
          {{ formatDate(filters.dateRange[0], true) }} &ndash;
          {{ formatDate(filters.dateRange[1], true) }}
        </el-tag>
        <button
          class="aps-filters__clear"
          @click="
            () => {
              filters.type = []
              filters.format = 'All'
              filters.organiser = 'All'
              filters.dateRange = null
            }
          "
        >
          Clear all
        </button>
      </div>

      <el-dropdown ref="dropdownSort" class="el-dropdown--sort" placement="bottom-end" trigger="click" :hide-on-click="false">
        <span class="aps-dropdown">
          Sort: {{ sortItems[sort] }}
          <svg class="aps-dropdown__chevron">
            <use xlink:href="#icon-chevron" />
          </svg>
        </span>
        <template #dropdown>
          <div class="aps-dropdown__header">
            <span class="aps-dropdown__title">Sort</span>
            <button class="aps-dropdown__close" @click="closeDropdown('dropdownSort')">
              <svg>
                <use xlink:href="#icon-close" />
              </svg>
            </button>
          </div>
          <el-dropdown-menu class="el-dropdown-menu--sort">
            <button
              v-for="sortId in Object.keys(sortItems)"
              :key="sortId"
              class="aps-cpd-results__sort"
              :class="{ 'aps-cpd-results__sort--active': sort === sortId }"
              @click="
                () => {
                  closeDropdown('dropdownSort')
                  sort = sortId
                }
              "
            >
              {{ sortItems[sortId] }}
            </button>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <template v-if="sort === 'organiser'">
      <div
        v-if="(filters.organiser === 'All' || filters.organiser === 'APS') && filteredCards.aps && filteredCards.aps.length"
        class="aps-cpd-results__cards aps-cpd-results__cards--aps"
      >
        <h2 v-if="filters.organiser === 'All'">APS and CPD Approved</h2>
        <aps-cpd-card v-for="(card, i) in filteredCards.aps.slice(0, numVisibleCards.aps)" :key="i" v-bind="card" />
        <button
          v-if="filteredCards.aps.length - numVisibleCards.aps > 0"
          class="aps-cpd-results__more"
          @click.prevent="
            () => {
              numVisibleCards.aps += 12
              setURLSearchParams()
            }
          "
        >
          More from APS and CPD Approved ({{ filteredCards.aps.length - numVisibleCards.aps }})
        </button>
      </div>
      <div
        v-if="(filters.organiser === 'All' || filters.organiser === 'Member') && filteredCards.member && filteredCards.member.length"
        class="aps-cpd-results__cards aps-cpd-results__cards--member"
      >
        <h2 v-if="filters.organiser === 'All'">Member Groups</h2>
        <aps-cpd-card v-for="(card, i) in filteredCards.member.slice(0, numVisibleCards.member)" :key="i" v-bind="card" />
        <button
          v-if="filteredCards.member.length - numVisibleCards.member > 0"
          class="aps-cpd-results__more"
          @click.prevent="
            () => {
              numVisibleCards.member += 12
              setURLSearchParams()
            }
          "
        >
          More from Member Groups ({{ filteredCards.member.length - numVisibleCards.member }})
        </button>
      </div>
      <div
        v-if="(filters.organiser === 'All' || filters.organiser === 'Third-party') && filteredCards.thirdParty && filteredCards.thirdParty.length"
        class="aps-cpd-results__cards aps-cpd-results__cards--third-party"
      >
        <h2 v-if="filters.organiser === 'All'">Third Party Providers</h2>
        <aps-cpd-card v-for="(card, i) in filteredCards.thirdParty.slice(0, numVisibleCards.thirdParty)" :key="i" v-bind="card" />
        <button
          v-if="filteredCards.thirdParty.length - numVisibleCards.thirdParty > 0"
          class="aps-cpd-results__more"
          @click.prevent="
            () => {
              numVisibleCards.thirdParty += 12
              setURLSearchParams()
            }
          "
        >
          More from Third Party Providers ({{ filteredCards.thirdParty.length - numVisibleCards.thirdParty }})
        </button>
      </div>
    </template>

    <div v-else-if="Array.isArray(filteredCards) && numResults > 0" class="aps-cpd-results__cards">
      <aps-cpd-card v-for="(card, i) in renderedCards" :key="i" v-bind="card" />
    </div>

    <div v-else class="aps-cpd-results__message-container">
      <span v-if="isLoadingCards" class="aps-cpd-results__loader"></span>
      <p v-if="locationsMessage" class="aps-cpd-results__message">
        {{ locationsMessage }}
      </p>
      <p v-if="cardsMessage" class="aps-cpd-results__message">
        {{ cardsMessage }}
      </p>
    </div>
  </section>
  <!-- <div class="aps-dropdown__overlay"></div> -->
</template>

<script>
import { ref, nextTick } from 'vue'
import ApsCpdCard from '../aps-cpd-card/aps-cpd-card.vue'

export default {
  name: 'aps-cpd-results',
  components: {
    ApsCpdCard,
  },
  data() {
    const initialNumRenderedCards = 12
    return {
      initialScrollPos: 0,
      initialNumRenderedCards,
      // Used for infinite scrolling when sort !== 'organiser'
      numRenderedCards: 0,
      // Used for 'More from' when sort === 'organiser'
      numVisibleCards: {
        aps: initialNumRenderedCards,
        member: initialNumRenderedCards,
        thirdParty: initialNumRenderedCards,
      },
      locationsMessage: '',
      cardsMessage: '',
      searchInput: ref([]),
      cards: ref([]),
      filters: {
        type: ref([]),
        format: ref('All'),
        organiser: ref('All'),
        location: ref(null),
        dateRange: ref(null),
        isApproved: ref(false),
      },
      isFiltersExpanded: false,
      isLoadingLocations: false,
      isLoadingCards: false,
      locations: [],
      eventTypes: [],
      sort: ref('organiser'),
      sortItems: {
        organiser: 'Group by Organiser / CPD Approved',
        'date-asc': 'Event Date (closest)',
        'date-desc': 'Event Date (farthest)',
        'cpd_hours-asc': 'CPD Hours (low - high)',
        'cpd_hours-desc': 'CPD Hours (high - low)',
      },
      organiserItems: {
        All: 'All',
        APS: 'APS',
        Member: 'Member Groups',
        'Third-party': 'Third Party Providers',
      },
      reqHeaders: {
        Accept: 'application/json',
        'Sec-Fetch-Dest': 'empty',
        'Sec-Fetch-Mode': 'cors',
        'Sec-Fetch-Site': 'same-origin',
      },
      ads: [],
    }
  },

  computed: {
    // Filters and sorts the cards returned by the API.
    // If sort === 'organiser' it returns an object with three keys, otherwise returns an array.
    filteredCards() {
      let cards = this.cards
        .filter(card => (this.filters.type.length ? this.filters.type.includes(card.type) : true))
        .filter(card => (this.filters.format === 'All' ? true : this.filters.format === card.format.split(' ').join('-')))
        .filter(card => (this.filters.organiser === 'All' ? true : this.filters.organiser === card.organiser_type))
        .filter(card => (this.filters.isApproved ? card.is_cpd_approved : true))
        .filter(card => {
          return this.filters.location !== null
            ? card.events &&
                card.events.some(
                  event =>
                    event.location.town === this.filters.location.town &&
                    event.location.state === this.filters.location.state &&
                    event.location.postCode === this.filters.location.postCode
                )
            : true
        })
        .filter(card => {
          if (this.filters.dateRange) {
            let rangeTimestampArr = this.filters.dateRange.map(date => date.getTime())
            return (
              card.events &&
              card.events.some(event => {
                let timestamp = new Date(event.date).getTime()
                return timestamp >= rangeTimestampArr[0] && timestamp <= rangeTimestampArr[1]
              })
            )
          }
          return true
        })
        .sort((card1, card2) => {
          let card1Date, card2Date
          switch (this.sort) {
            case 'cpd_hours-asc':
              return (card1.cpd_hours || 1000000000) - (card2.cpd_hours || 1000000000)
            case 'cpd_hours-desc':
              return (card2.cpd_hours || 0) - (card1.cpd_hours || 0)
            case 'date-desc':
              const distantPast = '0001-01-01T00:00:00.000Z'
              card1Date = card1.events && card1.events.length && card1.NearestEventDate ? card1.NearestEventDate : distantPast
              card2Date = card2.events && card2.events.length && card2.NearestEventDate ? card2.NearestEventDate : distantPast
              return card1Date < card2Date ? 1 : -1
            default:
              const distantFuture = '9999-12-31T00:00:00.000Z'
              card1Date = card1.events && card1.events.length && card1.NearestEventDate ? card1.NearestEventDate : distantFuture
              card2Date = card2.events && card2.events.length && card2.NearestEventDate ? card2.NearestEventDate : distantFuture
              return card1Date > card2Date ? 1 : -1
          }
        })
      // When sorting by 'organiser', cards are grouped into three categories:
      // 1. 'APS and CPD Approved'
      // 2. 'Member Groups'
      // 3. 'Third Party Providers'
      if (this.sort === 'organiser') {
        // When sorting by 'organiser', each category is sub-sorted:
        // 1. Closest events (by date) first (already done by default sort in switch statement above)
        // 2. Every fourth event is a random one that does not have an event date set
        const subSort = c => {
          let subSorted = []
          const withoutDate = []
          c.forEach(card => (card.events && card.events.length ? subSorted.push(card) : withoutDate.push(card)))
          withoutDate.sort(() => Math.random() - 0.5)
          for (let i = 0; i < withoutDate.length; i++) {
            if ((i + 1) * 4 <= subSorted.length) {
              subSorted.splice((i + 1) * 4 - 1, 0, withoutDate[i])
            } else {
              subSorted = [...subSorted, ...withoutDate.slice(i)]
              break
            }
          }
          return subSorted
        }
        const apsCpdCards = this.insertAds(subSort(cards.filter(card => card.is_cpd_approved || card.organiser_type === 'APS')))
        const memberCards = this.insertAds(subSort(cards.filter(card => card.organiser_type === 'Member')))
        const thirdPartyCards = this.insertAds(subSort(cards.filter(card => card.organiser_type === 'Third-party')))
        cards = {
          aps: apsCpdCards,
          member: memberCards,
          thirdParty: thirdPartyCards,
        }
        this.numRenderedCards = 0 // Side effect
      } else {
        this.numRenderedCards = Math.min(this.initialNumRenderedCards, cards.length) // Side effect
      }
      return cards
    },

    // Total number of filtered results, including unrendered
    numResults() {
      return this.sort === 'organiser'
        ? this.filteredCards.aps.length + this.filteredCards.member.length + this.filteredCards.thirdParty.length
        : this.filteredCards.length
    },

    // Used for infinite scrolling (when sort !== 'organiser')
    renderedCards() {
      return this.sort === 'organiser' ? [] : this.filteredCards.slice(0, this.numRenderedCards)
    },

    // Encodes search input and non-default filters as URL query params
    urlEncodedSearchParams() {
      let paramStr = '?'
      if (this.searchInput !== '') paramStr += `s=${this.searchInput}&`
      if (this.filters.type.length) paramStr += `type=${this.filters.type.join(',')}&`
      if (this.filters.format !== 'All') paramStr += `format=${this.filters.format}&`
      if (this.filters.organiser !== 'All') paramStr += `organiser=${this.filters.organiser}&`
      if (this.filters.location !== null)
        paramStr += `town=${this.filters.location.town}&state=${this.filters.location.state}&post_code=${this.filters.location.postCode}&`
      if (this.filters.dateRange) {
        let date = this.filters.dateRange[0]
        paramStr += `start_date=${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}&`
        date = this.filters.dateRange[1]
        paramStr += `end_date=${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}&`
      }
      if (this.filters.isApproved) paramStr += `is_approved=true&`
      if (this.sort === 'organiser') {
        if (this.numVisibleCards.aps > this.initialNumRenderedCards) paramStr += `num_aps-cpd_cards=${this.numVisibleCards.aps}&`
        if (this.numVisibleCards.member > this.initialNumRenderedCards) paramStr += `num_member_cards=${this.numVisibleCards.member}&`
        if (this.numVisibleCards.thirdParty > this.initialNumRenderedCards) paramStr += `num_third-party_cards=${this.numVisibleCards.thirdParty}&`
      } else {
        paramStr += `sort=${this.sort}`
      }
      if (paramStr.charAt(paramStr.length - 1) === '&') paramStr = paramStr.substring(0, paramStr.length - 1)
      return encodeURI(paramStr)
    },
  },

  watch: {
    filters: {
      handler: function () {
        if (this.sort === 'organiser') {
          // If filters are changed then results change too, so reset visible cards in each category
          this.numVisibleCards.aps = this.initialNumRenderedCards
          this.numVisibleCards.member = this.initialNumRenderedCards
          this.numVisibleCards.thirdParty = this.initialNumRenderedCards
        }
      },
      deep: true,
    },
    filteredCards() {
      // Also clear cardsMessage and update URL params when filteredCards change
      this.cardsMessage = ''
      this.setURLSearchParams()
    },
  },

  created() {
    if (window.aps.cpdEventTypes) {
      this.eventTypes = window.aps.cpdEventTypes
    } else {
      console.error('cpdEventTypes is missing!')
    }
    this.getPromoAds()
    this.setSearchParamsFromURL()
    this.getResults()
  },

  mounted() {
    // Infinite scroll through cards
    function handleIntersection(entries) {
      if (this.sort !== 'organiser' && entries[0].isIntersecting && this.numRenderedCards < this.numResults) {
        // Limit value to numResults
        this.numRenderedCards = Math.min(this.numRenderedCards + 3, this.numResults) // 3 is number of cards in one row
      }
    }
    const target = document.querySelector('.FooterBlock')
    const options = {
      root: null,
      rootMargin: '30px',
      threshold: 0, // Top of footer
    }
    const observer = new IntersectionObserver(handleIntersection.bind(this), options)
    observer.observe(target)

    window.addEventListener('beforeunload', () => {
      let href = window.location.href
      if (this.sort === 'organiser') {
        // If 'More from' has been clicked, remember numVisibleCards by appending to URL in location history
        if (this.numVisibleCards.aps > this.initialNumRenderedCards) href += `&num_aps-cpd_cards=${this.numVisibleCards.aps}`
        if (this.numVisibleCards.member > this.initialNumRenderedCards) href += `&num_member_cards=${this.numVisibleCards.member}`
        if (this.numVisibleCards.thirdParty > this.initialNumRenderedCards) href += `&num_third-party_cards=${this.numVisibleCards.thirdParty}`
      } else if (this.numRenderedCards > this.initialNumRenderedCards) {
        // If using infinite scroll, remember numRenderedCards by appending to URL in location history
        href += `&num_cards=${this.numRenderedCards}`
      }
      if (href !== window.location.href) {
        href += `&scroll_pos=${window.scrollY}`
        history.replaceState(null, '', href)
      }
    })

    // Focus the submit button when Tab is pressed in form input
    document.querySelector('.aps-cpd-results__form .el-select__tags').addEventListener('keydown', ev => {
      if (ev.key === 'Tab' || ev.keyCode === 9) {
        ev.preventDefault()
        document.querySelector('.aps-cpd-results__submit').focus()
      }
    })
    // document.querySelector('.el-select--location .el-input__inner').addEventListener('ontouchstart', ev => {
    //   ev.target.removeAttribute('readonly');
    //   ev.target.contentEditable = true
    // })
    // document.querySelector('.el-select--location .el-input__inner').addEventListener('focus', ev => {
    //   ev.target.removeAttribute('readonly');
    //   ev.target.contentEditable = true
    // })
  },

  methods: {
    closeDropdown(dropdown) {
      this.$refs[dropdown].handleClose()
    },

    // Dates before today are disabled in the date picker
    isDisabledDate(date) {
      return date.getTime() < new Date().setHours(0, 0, 0, 0)
    },

    // Format date for filter display, e.g. 12 Jan 2024.
    // If truncMonth boolean is specified, the month is shortened to 3 chars.
    formatDate(str, truncMonth) {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      const date = new Date(str)
      let month = months[date.getMonth()]
      if (truncMonth) {
        month = month.substring(0, 3)
      }
      return `${date.getDate()} ${month} ${date.getFullYear()}`
    },

    focusInput() {
      const innerInput = document.querySelector('.el-select--location .el-input__inner')
      innerInput.removeAttribute('readonly')
      innerInput.focus()
    },

    clearLocations() {
      this.filters.location = null
      this.locations = []
    },

    getLocations(str) {
      this.isLoadingLocations = true
      this.locationsMessage = ''
      const search = '?s=' + encodeURIComponent(str)

      fetch(`/aps/handlers/localityjson.ashx${search}`, {
        headers: this.reqHeaders,
      })
        .then(res => res.json())
        .then(resData => {
          // Add id key to each location object (for use with el-select value-key prop)
          this.locations = resData.map((l, i) => ({ ...l, id: i }))
          this.isLoadingLocations = false
        })
        .catch(err => {
          this.locationsMessage = err || 'Unable to find location'
          this.isLoadingLocations = false
        })
    },

    getResults(arr = [...this.searchInput]) {
      this.isLoadingCards = true
      this.cardsMessage = 'Loading events...'
      const search = '?s=' + encodeURIComponent(arr.join(','))

      fetch(`/aps/handlers/cpdsearchjson.ashx${search}`, {
        headers: this.reqHeaders,
      })
        .then(res => {
          if (res.ok) {
            return res.json()
          } else {
            throw Error
          }
        })
        .then(resData => {
          this.isLoadingCards = false
          this.cardsMessage = ''
          this.cards = resData
          // Restore previous scrollY position
          if (this.initialScrollPos) {
            nextTick(() => {
              window.scrollTo(0, this.initialScrollPos)
            })
          }
        })
        .catch(err => {
          this.cardsMessage = err.message || 'Sorry, there was a problem loading events. Please check your connection and try again.'
          this.isLoadingCards = false
        })
    },

    getPromoAds() {
      fetch('/aps/handlers/cpdadlistjson.ashx', { headers: this.reqHeaders })
        .then(res => {
          if (res.ok) {
            return res.json()
          } else {
            throw Error
          }
        })
        .then(resData => {
          this.ads = resData.filter(ad => !ad.hide_from_search_list)
        })
        .catch(err => {
          // Error with promo cards - does not impair functionality, do nothing
        })
    },

    // Takes encoded search params and updates the URL.
    // Necessary for bookmarking and infinite scrolling.
    setURLSearchParams() {
      history.replaceState(null, '', window.location.pathname + this.urlEncodedSearchParams)
    },

    setSearchParamsFromURL() {
      const searchParams = new URLSearchParams(document.location.search)
      const searchQuery = searchParams.get('s')
      const typeQuery = searchParams.get('type')
      const formatQuery = searchParams.get('format')
      const organiserQuery = searchParams.get('organiser')
      const townQuery = searchParams.get('town')
      const stateQuery = searchParams.get('state')
      const postCodeQuery = searchParams.get('post_code')
      const startDateQuery = searchParams.get('start_date')
      const endDateQuery = searchParams.get('end_date')
      const isApprovedQuery = searchParams.get('is_approved')
      const sortQuery = searchParams.get('sort')
      const numCardsQuery = searchParams.get('num_cards')
      const numApsCardsQuery = searchParams.get('num_aps-cpd_cards')
      const numMemberCardsQuery = searchParams.get('num_member_cards')
      const numThirdPartyCardsQuery = searchParams.get('num_third-party_cards')
      const scrollPosQuery = searchParams.get('scroll_pos')

      if (searchQuery) {
        this.searchInput = searchQuery.split(',')
      }
      if (typeQuery) {
        this.filters.type = typeQuery.split(',')
      }
      if (formatQuery) {
        this.filters.format = formatQuery
      }
      if (organiserQuery) {
        this.filters.organiser = organiserQuery
      }
      if (townQuery && stateQuery && postCodeQuery) {
        this.locations = [{ town: townQuery, state: stateQuery, postCode: postCodeQuery }]
        this.filters.location = {
          town: townQuery,
          state: stateQuery,
          postCode: postCodeQuery,
        }
      }
      if (startDateQuery && endDateQuery) {
        let startDate = new Date()
        let endDate = new Date()
        let dateArr = startDateQuery.split('-')
        startDate.setYear(dateArr[0])
        startDate.setDate(dateArr[2])
        startDate.setMonth(dateArr[1] - 1)
        dateArr = endDateQuery.split('-')
        endDate.setYear(dateArr[0])
        endDate.setDate(dateArr[2])
        endDate.setMonth(dateArr[1] - 1)
        this.filters.dateRange = [startDate, endDate]
      }
      if (isApprovedQuery) {
        this.filters.isApproved = true
      }
      if (sortQuery) {
        this.sort = sortQuery
      }
      if (numCardsQuery) {
        this.initialNumRenderedCards = parseInt(numCardsQuery)
      }
      if (!sortQuery || sortQuery === 'organiser') {
        if (numApsCardsQuery) this.numVisibleCards.aps = parseInt(numApsCardsQuery)
        if (numMemberCardsQuery) this.numVisibleCards.member = parseInt(numMemberCardsQuery)
        if (numThirdPartyCardsQuery) this.numVisibleCards.thirdParty = parseInt(numThirdPartyCardsQuery)
      }
      if (scrollPosQuery) {
        this.initialScrollPos = parseInt(scrollPosQuery)
      }
    },

    handleSearch(ev) {
      if (ev) {
        ev.preventDefault()
      }
      this.setURLSearchParams()
      this.getResults()
    },

    // Inserts promo cards into an array of event cards - 1 every 9 cards - and returns a new array
    insertAds(cards) {
      let newCards = []
      for (let i = 0; i < cards.length; i++) {
        if ((i + 1) % 9 === 0) {
          let randomAd = this.ads[Math.floor(Math.random() * this.ads.length)]
          if (randomAd) {
            newCards.push(randomAd)
          }
        }
        newCards.push(cards[i])
      }
      return newCards
    },

    // Adds current value of input (if present) to query params, otherwise it would not be captured if not in a tag
    captureInput() {
      if (this.$refs.$searchInput.query) {
        this.searchInput.push(this.$refs.$searchInput.query)
      }
    },

    showOverlay(isVisible) {
      if (window.innerWidth <= 992) {
        const body = document.querySelector('body')
        // const overlay = document.querySelector('.aps-dropdown__overlay')
        if (isVisible) {
          body.style.position = 'relative'
          // overlay.style.display = 'block'
        } else {
          body.style.position = 'static'
          // overlay.style.display = 'none'
        }
      }
    },

    setFocus(el) {
      el.target.blur()
      el.target.readOnly = false
      el.target.focus()
    },
  },
}
</script>

<style lang="scss">
@import './aps-cpd-results.scss';
</style>
